/**
 * <ExecutiveBriefView /> — leave-behind one-pager + Share / Export PDF.
 *
 * The sales rep sends this to the prospect after discovery + demo. Executive
 * enough to forward internally, written TO the prospect (second person).
 * Reuses the discovery-report share model (URL fragment, public /r, no login)
 * and the same print-isolate PDF path.
 */
import { useRef, useState } from 'react';
import MoxoLogo from './MoxoLogo';
import { buildPublicBriefSharePayload } from '../../lib/share-brief';
import { buildShareUrl } from '../../lib/share-report';
import type { ExecutiveBriefResult } from './executive-brief-types';

const money = (x: number): string =>
  `${x < 0 ? '-' : ''}$${Math.abs(Math.round(x)).toLocaleString('en-US')}`;
const roundInt = (x: number): string => Math.round(x).toLocaleString('en-US');
const oneDp = (x: number): string => x.toFixed(1);
const peopleOfTime = (n: number): string =>
  `about ${oneDp(n)} ${Math.abs(n - 1) < 0.05 ? 'person' : 'people'} of time`;

export default function ExecutiveBriefView({
  result,
  processName,
  showSources = true,
  showActions = true,
}: {
  result: ExecutiveBriefResult;
  processName: string;
  showSources?: boolean;
  showActions?: boolean;
}) {
  const brief = result.brief ?? null;
  const reportRef = useRef<HTMLElement | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [shareCopied, setShareCopied] = useState(false);

  if (!brief) return null;

  const sharePayload = buildPublicBriefSharePayload(result, processName);

  function handleExportPdf() {
    document.body.classList.add('se-print-isolate');
    const cleanup = () => {
      document.body.classList.remove('se-print-isolate');
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    window.print();
    setTimeout(cleanup, 1500);
  }

  function handlePresent() {
    const url = buildShareUrl(sharePayload).replace('/r#', '/r?present=1#');
    const win = window.open(url, '_blank');
    if (!win) window.location.assign(url);
  }

  function handleShare() {
    const url = buildShareUrl(sharePayload);
    setShareUrl(url);
    setShareCopied(false);
    void navigator.clipboard
      ?.writeText(url)
      .then(() => setShareCopied(true))
      .catch(() => setShareCopied(false));
  }

  const roi = result.roi;
  const sources = result.sources;
  const sellingPoints = brief.sellingPoints.filter((s) => s.source !== 'inferred');

  return (
    <article className="se-report se-brief" ref={reportRef}>
      {showActions && (
      <>
      <div className="se-actions no-print no-share">
        <button type="button" className="se-action se-action--primary" onClick={handlePresent}>
          Present ↗
        </button>
        <button type="button" className="se-action" onClick={handleExportPdf}>
          Export PDF
        </button>
        <button type="button" className="se-action" onClick={handleShare}>
          Share link
        </button>
      </div>

      {shareUrl && (
        <div className="se-sharebar no-print no-share" role="status">
          <span className="se-sharebar__msg">
            {shareCopied ? 'Link copied.' : 'Your link is ready.'} Anyone with the link can open it — no login. Send
            it to the prospect.
          </span>
          <div className="se-sharebar__row">
            <input
              className="se-sharebar__input"
              type="text"
              value={shareUrl}
              readOnly
              onFocus={(e) => e.currentTarget.select()}
              aria-label="Shareable executive brief link"
            />
            <button
              type="button"
              className="se-action"
              onClick={() => {
                void navigator.clipboard?.writeText(shareUrl).then(() => setShareCopied(true));
              }}
            >
              {shareCopied ? 'Copied' : 'Copy'}
            </button>
            <a className="se-action" href={shareUrl} target="_blank" rel="noopener noreferrer">
              Open
            </a>
          </div>
        </div>
      )}
      </>
      )}

      {showSources && sources && (
        <p className="se-brief__sources no-print">
          Built from
          {sources.discoveryReport ? ' the discovery report' : ''}
          {sources.discoveryNotes ? `${sources.discoveryReport ? ',' : ''} discovery notes` : ''}
          {sources.demoNotes ? `${sources.discoveryReport || sources.discoveryNotes ? ',' : ''} demo notes` : ''}
          {sources.hubspotActivities > 0
            ? `${sources.discoveryReport || sources.discoveryNotes || sources.demoNotes ? ',' : ''} ${sources.hubspotActivities} HubSpot call note${sources.hubspotActivities === 1 ? '' : 's'}`
            : ''}
          {!sources.discoveryReport &&
          !sources.discoveryNotes &&
          !sources.demoNotes &&
          sources.hubspotActivities === 0
            ? ' company research (no call notes on file)'
            : ''}
          .
        </p>
      )}

      <div className="se-brandhead print-share-only">
        <MoxoLogo size={30} />
        <span className="se-brandhead__label">Executive Brief</span>
      </div>

      {showSources && brief.lowSignal && (
        <div className="se-note se-note--warning" role="status">
          Limited signal — verify details before you send this.
        </div>
      )}

      <header className="se-hero se-brief__hero">
        <div className="se-hero__eyebrow">Executive brief · {processName || 'Use case'}</div>
        <h1 className="se-hero__name se-brief__headline">{brief.headline}</h1>
        <p className="se-brief__addressed">{brief.addressedTo}</p>
        <p className="se-hero__summary">{brief.companySnapshot}</p>
      </header>

      <section className="se-block se-brief__section">
        <h2 className="se-section-title">The use case</h2>
        <h3 className="se-brief__usetitle">{brief.mainUseCase.title}</h3>
        <p className="se-brief__prose">{brief.mainUseCase.narrative}</p>
      </section>

      <section className="se-block se-brief__section">
        <h2 className="se-section-title">What hurts today</h2>
        <ul className="se-brief__list">
          {brief.painToday.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </section>

      <section className="se-block se-brief__section">
        <h2 className="se-section-title">What Moxo changes</h2>
        <ol className="se-brief__benefits">
          {brief.moxoBenefits.map((b) => (
            <li key={b.title} className="se-brief__benefit">
              <strong>{b.title}</strong>
              <span>{b.whyItMatters}</span>
            </li>
          ))}
        </ol>
      </section>

      {roi && (
        <section className="se-block se-brief__section se-brief__roi">
          <h2 className="se-section-title">Estimated impact</h2>
          <p className="se-brief__prose">{brief.roiNarrative}</p>
          <div className="se-brief__tiles" data-testid="brief-roi-tiles">
            <div className="se-tile">
              <div className="se-tile__label">Hours freed / year</div>
              <div className="se-tile__value">{roundInt(roi.annualHoursFreed)}</div>
              <div className="se-tile__sub">{peopleOfTime(roi.fteEquivalent)}</div>
            </div>
            <div className="se-tile">
              <div className="se-tile__label">Labor time saved</div>
              <div className="se-tile__value">{money(roi.laborSavings)}</div>
              <div className="se-tile__sub">Conservative case · regional rate</div>
            </div>
            <div className="se-tile">
              <div className="se-tile__label">Days saved per run</div>
              <div className="se-tile__value">{roundInt(roi.cycleDaysSaved)}</div>
              <div className="se-tile__sub">About {roundInt(roi.cycleDaysSaved)} fewer days each time</div>
            </div>
          </div>
          <p className="se-brief__roi-foot">
            Conservative estimate using regional labor rates. A starting point to measure on your
            real process during a pilot — not a guarantee.
          </p>
        </section>
      )}

      {sellingPoints.length > 0 && (
      <section className="se-block se-brief__section">
        <h2 className="se-section-title">What we heard</h2>
        <ul className="se-brief__list se-brief__selling">
          {sellingPoints.map((s) => (
            <li key={s.point}>{s.point}</li>
          ))}
        </ul>
      </section>
      )}

      <section className="se-block se-brief__section">
        <h2 className="se-section-title">Recommended next step</h2>
        <p className="se-brief__prose se-brief__next">{brief.recommendedNextStep}</p>
      </section>
    </article>
  );
}
